import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { PageableTableComponent } from '../../../shared/controls/pageable-table/pageable-table.component';
import { AutoCompleteDropdownComponent } from '../../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';

import { DialogService } from '../../../shared/controls/message-dialog/message-dialog.service';
import { ToastService } from '../../../shared/toast/toast.service';
import { ErrorService } from '../../../shared/services/error-service';
import { InitializerService } from '../../../shared/services/initializer-service';
import { AuthService } from '../../../shared/services/auth.service';
import { LoadingService } from '../../../shared/services/loading.service';
import { LoadingHelper } from '../../../shared/services/loading-helper';

import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { AreaConstants } from '../../../shared/constants/area.constants';
import { PermissionConstants } from '../../../shared/constants/permission.constants';

import { PremiumDataService, PeriodService, PagingData, AllPeriodItem, PremiumDataBatchesItem } from '../../../../service/proxy';

@Component({
    templateUrl: './premium-data-list.component.html',
    styleUrls: ['./premium-data-list.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        PageableTableComponent,
        AutoCompleteDropdownComponent,
        HasPermissionDirective
    ],

    providers: [
        PeriodService,
        PremiumDataService
    ]
})

export class PremiumDataComponent implements OnInit {
    public initialized = false;

    public selectedStatus: boolean = false;
    public selectedPeriodId: number = 0;
    public search: string = '';

    public paging: PagingData = new PagingData();
    public periods: AllPeriodItem[] = [];
    public batches: PremiumDataBatchesItem[] = [];

    public statusOptions = [
        { id: 0, label: 'Pending' },
        { id: 1, label: 'Completed' }
    ];

    public showEdit: boolean = false;
    public showDelete: boolean = false;

    constructor(
        public authService: AuthService,
        private router: Router,
        private dialogService: DialogService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private initializerService: InitializerService,
        private periodService: PeriodService,
        private premiumDataService: PremiumDataService)
    {
        this.paging = this.initializerService.createPagingData();
    }

    ngOnInit(): void {
        this.periodService.getAllPeriods()
            .subscribe((data) => {
                this.initialized = true;
                this.periods = data.results || [];
                this.selectedPeriodId = this.periods[1]?.id ?? 0;

                this.initializePermissions();
                this.refreshGridData();
            }
        );
    }

    initializePermissions(): void {
        const isAdmin = this.authService.hasPermission(AreaConstants.Annexure, PermissionConstants.DeleteCompleted);
        const selectedPeriod = this.periods.find(p => p.id === this.selectedPeriodId);

        let deleteWindow = false;

        if (selectedPeriod) {
            const periodStart = new Date(selectedPeriod.year, selectedPeriod.month - 1, 1);
            const now = new Date();
            const windowEnd = new Date(periodStart);
            windowEnd.setMonth(windowEnd.getMonth() + 2);

            deleteWindow = now >= periodStart && now <= windowEnd;
        }

        this.showEdit =
            this.selectedStatus === false && this.authService.hasPermission(AreaConstants.Annexure, PermissionConstants.Import);

        this.showDelete =
            isAdmin ||
            (
                this.selectedStatus === false &&
                this.authService.hasPermission(AreaConstants.Annexure, PermissionConstants.DeletePending) &&
                deleteWindow
            );
    }

    formatPeriod = (period: AllPeriodItem): string =>
        `${period?.year}-${String(period?.month).padStart(2, '0')}`;

    displayStatus = (status: any): string => status?.label ?? '';

    onStatusSelectionChange(statusId: number | null): void {
        this.selectedStatus = statusId === 1;
        this.refreshGridData();
        this.initializePermissions();
    }

    onPeriodSelectionChange(periodId: number | null): void {
        this.selectedPeriodId = periodId ?? 0;
        this.refreshGridData();
    }

    getPremiumBatches(pagingData: PagingData = new PagingData): void {
        if (!this.initialized) {
            return;
        }

        const search = `${this.selectedPeriodId}|${this.search}`;

        this.premiumDataService.getFilteredPremiumDataBatches(this.selectedStatus, search, pagingData.pageNo, pagingData.pageSize, pagingData.sortDirection, pagingData.sortColumn)
            .subscribe({
                next: (data: any) => {
                    this.paging = data.pagingData;
                    this.batches = data.results || [];
                },
                error: (error: any) => {
                    if (error.status === 204) {
                        this.batches = [];
                        this.paging = this.initializerService.createPagingData();
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                }
            }
        );
    }

    getColumnMap(): Record<string, string> {
        const baseColumnMap: Record<string, string> = {
            createdAt: 'Date Created',
            fileName: 'Filename',
            affectedPolicies: 'Effected Policies',
            insurerName: 'Insurer',
            period: 'Period'
        };

        //Append Date Completed/Updated based on status
        return this.selectedStatus
            ? { ...baseColumnMap, completedAt: 'Date Completed' }
            : { ...baseColumnMap, updatedAt: 'Date Updated' };
    }

    refreshGridData(): void {
        this.paging = this.initializerService.createPagingData();
        this.getPremiumBatches(this.paging);
    }

    createBatch(): void {
        this.router.navigate(['/premium-data-create']);
    }

    editBatch(batchId: number): void {
        sessionStorage.setItem('isRedirected', true.toString());
        this.router.navigate([`/premium-data-wizard/${batchId}`]);
    }

    deletePremiumBatch(batchId: number): void {
        const title = this.selectedStatus
            ? 'Delete COMPLETED Batch'
            : 'Delete Pending Batch';

        const message = this.selectedStatus
            ? 'You are about to delete a COMPLETED batch. Are you sure?'
            : 'Are you sure you want to delete this batch?';

        const toastMessage = this.selectedStatus
            ? 'Completed batch deleted successfully.'
            : 'Pending batch deleted successfully.';

        if (!this.selectedStatus) {
            this.deletePendingPremiumBatch(batchId, title, message, toastMessage);
        }
        else {
            this.deleteCompletedPremiumBatch(batchId, title, message, toastMessage);
        };
    }

    deletePendingPremiumBatch(batchId: number, title: string, message: string, toastMessage: string): void {
        this.dialogService.show(title, message, 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.premiumDataService.deletePendingPremiumBatch(batchId)
                        .subscribe({
                            next: () => {
                                this.getPremiumBatches(this.paging);
                            },
                            error: (error: any) => {
                                this.errorService.handleError(error);
                            },
                            complete: () => {
                                this.toastService.show(toastMessage, 'success');
                            }
                        }
                    );
                }
            }
        );
    }

    deleteCompletedPremiumBatch(batchId: number, title: string, message: string, toastMessage: string): void {
        this.dialogService.show(title, message, 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.premiumDataService.deleteCompletedPremiumBatch(batchId)
                        .subscribe({
                            next: () => {
                                this.getPremiumBatches(this.paging);
                            },
                            error: (error: any) => {
                                this.errorService.handleError(error);
                            },
                            complete: () => {
                                this.toastService.show(toastMessage, 'success');
                            }
                        }
                    );
                }
            }
        );
    }
}
